import { useEffect, useMemo, useState } from 'react';
import JwtValidator from '../utils/JwtValidator';

interface SelectedCompany {
  id?: unknown;
  name?: unknown;
}

const toCompanyId = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const companyId = Number(value);
  return Number.isFinite(companyId) ? companyId : undefined;
};

const getSelectedCompany = (): SelectedCompany | undefined => {
  const selectedCompanyRaw = localStorage.getItem('selectedCompany');

  if (!selectedCompanyRaw) {
    return undefined;
  }

  try {
    return JSON.parse(selectedCompanyRaw) as SelectedCompany;
  } catch {
    console.error('Malformed selectedCompany in local storage');
    return undefined;
  }
};

export const getSelectedCompanyId = (): number | undefined => {
  return toCompanyId(getSelectedCompany()?.id);
};

export const getSelectedCompanyName = (): string | undefined => {
  const selectedCompanyName = getSelectedCompany()?.name;

  return typeof selectedCompanyName === 'string' && selectedCompanyName.trim() !== ''
    ? selectedCompanyName
    : undefined;
};

export const getCurrentCompanyId = (
  jwtValidator: JwtValidator = new JwtValidator()
): number | undefined => {
  if (jwtValidator.hasCompanySelectionRole()) {
    const selectedCompanyId = getSelectedCompanyId();

    if (selectedCompanyId !== undefined) {
      return selectedCompanyId;
    }
  }

  return toCompanyId(jwtValidator.decodedToken?.companyId);
};

export const useCurrentCompanyId = (): number | undefined => {
  const jwtValidator = useMemo(() => new JwtValidator(), []);
  return getCurrentCompanyId(jwtValidator);
};

export const useSelectedCompanyName = (): string | undefined => {
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>(() =>
    getSelectedCompanyName()
  );

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === 'selectedCompany') {
        setSelectedCompanyName(getSelectedCompanyName());
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return selectedCompanyName;
};
